import { observer } from 'mobx-react-lite';
import {
  Button,
  DataGridToolbar,
  DropDownWrap,
  Icon,
  showMessageBox,
} from '../../../../components';
import { useStore } from '../../../../store';
import PulseKeys from '../../store/PulseKeys';
import PulseKeySearch from '../../store/PulseKeySearch';

export default observer(function KeysGridToolbar({
  pulseKeys,
  search,
}: {
  pulseKeys: PulseKeys;
  search: PulseKeySearch;
}) {
  if (pulseKeys.selected.length === 0) {
    return <DataGridToolbar />;
  }

  return (
    <DataGridToolbar itemCount={pulseKeys.selected.length} itemName="key">
      <SetLabelButton pulseKeys={pulseKeys} search={search} />
      <MoveBetweenProductsButton pulseKeys={pulseKeys} search={search} />
      <ExportButton pulseKeys={pulseKeys} search={search} />
    </DataGridToolbar>
  );
});

function ExportButton({
  pulseKeys,
  search,
}: {
  pulseKeys: PulseKeys;
  search: PulseKeySearch;
}) {
  async function onClick() {
    pulseKeys.exportSelected();
    search.search();
  }

  return (
    <Button onClick={onClick} type="primaryText">
      Export
    </Button>
  );
}

function MoveBetweenProductsButton({
  pulseKeys,
  search,
}: {
  pulseKeys: PulseKeys;
  search: PulseKeySearch;
}) {
  const { pulseKeyStore } = useStore();

  const options = search.products.toOptions();

  async function onSelect(newProductId: string) {
    if (!newProductId) {
      return;
    }

    const productName = search.products.getProductDisplayName(newProductId);

    const buttonId = await showMessageBox({
      content: `Are you sure you want to transfer selected keys to ${productName}?`,
      title: 'Transferring PulseKeys to another product',
      buttons: [
        { icon: 'truck-moving', id: 'move', title: 'Transfer' },
        { id: 'cancel', title: 'Cancel' },
      ],
    });

    if (buttonId !== 'move') {
      return;
    }

    await pulseKeyStore.moveBetweenProducts({
      keyIds: pulseKeys.selected.map((p) => p.key),
      newProductId,
    });
    search.search();
  }

  return (
    <DropDownWrap onSelect={onSelect} options={options}>
      <Button size="small" type="primaryText">
        Change product <Icon icon="caret-down" sx={{ ml: 1, mr: 0 }} />
      </Button>
    </DropDownWrap>
  );
}

function SetLabelButton({
  pulseKeys,
  search,
}: {
  pulseKeys: PulseKeys;
  search: PulseKeySearch;
}) {
  const { pulseKeyStore } = useStore();
  const options = search.labels.getLabelOptions(<i>Not set</i>);

  async function onSelect(label: string) {
    await pulseKeyStore.setKeysLabel(pulseKeys.selected, label);
    search.search();
  }

  return (
    <DropDownWrap onSelect={onSelect} options={options}>
      <Button size="small" type="primaryText">
        Set label <Icon icon="caret-down" sx={{ ml: 1, mr: 0 }} />
      </Button>
    </DropDownWrap>
  );
}
